import { useEffect, useRef, useCallback, useState } from 'react';
import axios from 'axios';
import { MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BackgroundLocationTracker
 * ─────────────────────────────────────────────────────────────────────────
 * Starts automatically on sign-in. Keeps tracking until the employee
 * explicitly logs out. Handles:
 *  • Auto-start on mount (no button)
 *  • Immediate re-push when tab comes back to foreground (phone switch)
 *  • Auto-restart if watchPosition was killed by the OS
 *  • Heartbeat every 30 s
 *  • Nominatim reverse-geocoding → address tag line
 *  • Renders a tiny green "GPS active" dot in the top-right (visible to
 *    employee so they know they're being tracked)
 *
 * NOT tracked: CMD, super_admin, HR Head
 * ─────────────────────────────────────────────────────────────────────────
 */

// Address cache: coords rounded to 3 dp (≈ 111 m) → address string
const GEO_CACHE = {};
async function reverseGeocode(lat, lng) {
  const key = `${lat.toFixed(3)},${lng.toFixed(3)}`;
  if (GEO_CACHE[key]) return GEO_CACHE[key];
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=17&addressdetails=0`,
      { headers: { 'Accept-Language': 'en', 'User-Agent': 'DBK-CRM-Location/1.0' } }
    );
    if (!res.ok) return '';
    const json = await res.json();
    const addr = json?.display_name || '';
    GEO_CACHE[key] = addr;
    return addr;
  } catch { return ''; }
}

// ── Time window helpers (IST = UTC+5:30) ──────────────────────────────────────
function nowIST() {
  return new Date(Date.now() + (5.5 * 60 - new Date().getTimezoneOffset()) * 60_000);
}
function isWithinTrackingWindow() {
  const d = nowIST();
  const h = d.getUTCHours(), m = d.getUTCMinutes();
  const mins = h * 60 + m;
  return mins >= 9 * 60 + 45 && mins <= 22 * 60 + 30;  // 09:45 – 22:30 IST
}

// ── Distance helper (Haversine, metres) ───────────────────────────────────────
function distanceM(lat1, lng1, lat2, lng2) {
  const R = 6_371_000;
  const toRad = d => (d * Math.PI) / 180;
  const dφ = toRad(lat2 - lat1), dλ = toRad(lng2 - lng1);
  const a = Math.sin(dφ/2)**2 + Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dλ/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

const BackgroundLocationTracker = ({ user }) => {
  const watchIdRef     = useRef(null);
  const heartbeatRef   = useRef(null);
  const restartRef     = useRef(null);   // checks if watch is still alive
  const lastCoordsRef  = useRef(null);
  const lastAddressRef = useRef('');
  const lastUpdateRef  = useRef(0);      // epoch ms of last successful GPS fix
  const visHandlerRef  = useRef(null);
  const [gpsActive, setGpsActive] = useState(false);

  const skipTracking =
    !user?.email ||
    user.role === 'cmd' ||
    user.role === 'super_admin' ||
    (user.department === 'hr' && user.role === 'department_head');

  // ── push coords + address to the server ──────────────────────────────────
  const pushToServer = useCallback(async (lat, lng, acc, address) => {
    // Client-side window check — avoid unnecessary API calls
    if (!isWithinTrackingWindow()) return;

    const token = localStorage.getItem('crm_token');
    if (!token) return;

    // Client-side distance check: only push if moved ≥ 50 m from last sent
    const prev = lastCoordsRef.current;
    if (prev && distanceM(lat, lng, prev.lat, prev.lng) < 50) return;

    try {
      await axios.post(
        `${API}/location/update`,
        { lat, lng, accuracy: acc || 0, address: address || lastAddressRef.current || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      lastUpdateRef.current = Date.now();
    } catch { /* silent */ }
  }, []);

  // ── clear GPS watch + heartbeat safely ───────────────────────────────────
  const stopAll = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
    if (restartRef.current)   { clearInterval(restartRef.current);   restartRef.current   = null; }
    setGpsActive(false);
  }, []);

  // ── start (or restart) the GPS watch ─────────────────────────────────────
  const startWatch = useCallback(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, accuracy: acc } = pos.coords;
        lastCoordsRef.current = { lat, lng, acc };
        lastUpdateRef.current = Date.now();
        setGpsActive(true);

        const address = await reverseGeocode(lat, lng);
        lastAddressRef.current = address;
        pushToServer(lat, lng, acc, address);

        // Kill any stale watch before creating a new one
        if (watchIdRef.current !== null) {
          navigator.geolocation.clearWatch(watchIdRef.current);
        }
        watchIdRef.current = navigator.geolocation.watchPosition(
          async (p) => {
            const { latitude: la, longitude: lo, accuracy: ac } = p.coords;
            lastCoordsRef.current = { lat: la, lng: lo, acc: ac };
            lastUpdateRef.current = Date.now();
            const addr = await reverseGeocode(la, lo);
            if (addr) lastAddressRef.current = addr;
          },
          () => { setGpsActive(false); },
          { enableHighAccuracy: true, maximumAge: 60000, timeout: 20000 }
        );

        // Heartbeat every 30 s
        if (!heartbeatRef.current) {
          heartbeatRef.current = setInterval(() => {
            const c = lastCoordsRef.current;
            if (c) pushToServer(c.lat, c.lng, c.acc, lastAddressRef.current);
          }, 30_000);
        }
      },
      () => { setGpsActive(false); },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }, [pushToServer]);

  // ── auto-restart guard: if no GPS fix for >3 min, restart watchPosition ──
  const startRestartGuard = useCallback(() => {
    if (restartRef.current) clearInterval(restartRef.current);
    restartRef.current = setInterval(() => {
      const msSinceUpdate = Date.now() - lastUpdateRef.current;
      if (msSinceUpdate > 3 * 60 * 1000) {
        // OS may have killed watchPosition — restart it
        if (watchIdRef.current !== null) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }
        startWatch();
      }
    }, 60_000); // check every minute
  }, [startWatch]);

  // ── visibility change: push immediately when user returns to tab/app ──────
  const attachVisibilityHandler = useCallback(() => {
    if (visHandlerRef.current) {
      document.removeEventListener('visibilitychange', visHandlerRef.current);
    }
    visHandlerRef.current = () => {
      if (document.visibilityState !== 'visible') return;
      // App just came to foreground — restart if watch was killed
      if (watchIdRef.current === null) {
        startWatch();
        startRestartGuard();
      } else {
        // Watch may still be alive — push latest coords immediately
        navigator.geolocation.getCurrentPosition(
          async (p) => {
            const { latitude, longitude, accuracy } = p.coords;
            lastCoordsRef.current = { lat: latitude, lng: longitude, acc: accuracy };
            lastUpdateRef.current = Date.now();
            const addr = await reverseGeocode(latitude, longitude);
            if (addr) lastAddressRef.current = addr;
            pushToServer(latitude, longitude, accuracy, lastAddressRef.current);
            setGpsActive(true);
          },
          () => {
            // GPS unavailable right now — use last known
            const c = lastCoordsRef.current;
            if (c) pushToServer(c.lat, c.lng, c.acc, lastAddressRef.current);
          },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
        );
      }
    };
    document.addEventListener('visibilitychange', visHandlerRef.current);
  }, [pushToServer, startWatch, startRestartGuard]);

  // ── main mount effect ─────────────────────────────────────────────────────
  useEffect(() => {
    if (skipTracking) return;
    if (!navigator.geolocation) return;
    // Don't start tracking outside the 09:45–22:30 IST window
    if (!isWithinTrackingWindow()) return;

    const launch = () => {
      startWatch();
      startRestartGuard();
      attachVisibilityHandler();
    };

    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((res) => {
          if (res.state !== 'denied') launch();
          res.onchange = () => { if (res.state === 'granted') launch(); };
        })
        .catch(launch);
    } else {
      launch();
    }

    return () => {
      stopAll();
      if (visHandlerRef.current) {
        document.removeEventListener('visibilitychange', visHandlerRef.current);
        visHandlerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  if (skipTracking) return null;

  // Small status dot — visible to employee so they know tracking is on
  return (
    <div
      className="fixed bottom-4 right-4 z-[9990] pointer-events-none"
      data-testid="location-tracker-badge"
      title={gpsActive ? 'Location tracking active' : 'Location tracking starting…'}
    >
      <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full shadow-lg text-[10px] font-semibold
        ${gpsActive
          ? 'bg-green-600 text-white'
          : 'bg-slate-700 text-slate-200'}`}
      >
        <MapPin className="w-2.5 h-2.5" />
        <span className={`w-1.5 h-1.5 rounded-full ${gpsActive ? 'bg-green-300 animate-pulse' : 'bg-slate-400'}`} />
        {gpsActive ? 'GPS Active' : 'GPS…'}
      </div>
    </div>
  );
};

export default BackgroundLocationTracker;
